import React from 'react';

import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import StepTypeMenu from 'pipelines/components/StepTypeMenu';
import {stepTypeIsConfigEditorSupported} from 'pipelines/generalized-step-config-editor/stepTypeIsConfigEditorSupported';
import {PipelineStep, PipelineStepData} from 'pipelines/PipelineTypes';
import {getLatestStepVersion, getPossibleEdges} from 'pipelines/StepConnectionUtils';
import AddNodeHandle from 'ui-library/reactflow/handles/AddNodeHandle';

import {AnalysisFlowchartAction} from '../actions/AnalysisFlowchartActionTypes';
import AnalysisGraphUtils from '../analysis-graph/AnalysisGraphUtils';
import {useAnalysisFlowchartContext} from '../AnalysisFlowchartContext';

enum StepDataRole {
  INPUT,
  OUTPUT,
}

export interface StepDataHandleProps {
  step: PipelineStep;
  /** Step data the handle represents. */
  stepData: PipelineStepData;
}

/**
 * A handle, which represents an input or output step data on a step.
 *
 * In `reactflow`, "handles" are the components rendered where edges and nodes connect.
 */
export default function StepDataHandle({step, stepData}: StepDataHandleProps): JSX.Element {
  // Search by `id` rather than `name`, to not assume input and output step data names are disjoint
  const stepDataRole = step.inputStepDatas.find(({id}) => id === stepData.id)
    ? StepDataRole.INPUT
    : StepDataRole.OUTPUT;

  const {getActionInfo} = useAnalysisFlowchartContext();

  /**
   * Check the `ADD_STEP` action is enabled for all steps whose inputs would be updated by adding a step.
   *
   * This assumes clicking `StepDataHandle` adds a step inline; i.e. any other steps currently connected to
   * `stepData` will be re-connected to the new step. (Adding a step to a new branch wouldn't affect steps
   * currently connected to `stepData`, if it is an output step data.)
   */
  const stepsAddStepActionWouldUpdate =
    stepDataRole === StepDataRole.INPUT
      ? [step]
      : step.outgoingEdges
          .filter(({fromStepData}) => fromStepData.name === stepData.name)
          .map(({toStep}) => toStep);

  const {doAction: addStep, enabled: canAddStep} = getActionInfo(
    AnalysisFlowchartAction.ADD_STEP,
    ...stepsAddStepActionWouldUpdate.map(({id}) => id)
  );

  const canAddStepType = (newStepType: PipelineStepType): boolean => {
    // Don't allow adding steps which can't be configured in the generic way. If any step types can only be
    // added for a specific pipeline type, we may need to lift this filter up to the parent.
    if (!stepTypeIsConfigEditorSupported(newStepType, getLatestStepVersion(newStepType))) {
      return false;
    }

    const possibleEdges =
      stepDataRole === StepDataRole.INPUT
        ? getPossibleEdges({fromStepType: newStepType, toStepType: step.stepType, toStepData: stepData})
        : getPossibleEdges({toStepType: newStepType, fromStepType: step.stepType, fromStepData: stepData});

    // Checking for exactly one possible edge prevents adding steps if there is an ambiguous connection; we
    // can loosen this to >0 once edges can be drag-and-dropped, and let users fix any incorrect connections
    return possibleEdges.length === 1;
  };

  return (
    <AddNodeHandle
      id={AnalysisGraphUtils.getHandleId(stepData)}
      type={stepDataRole === StepDataRole.INPUT ? 'target' : 'source'}
      renderPopoverContent={() => (
        <StepTypeMenu
          filterStepTypes={(stepType) => canAddStepType(stepType)}
          onSelect={(stepType) => addStep?.(stepType, stepData)}
        />
      )}
      disabled={!canAddStep}
    />
  );
}
